import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrpcTanstackQueryUtils } from '../orpc'

export function useAircraftGetAll(input?: {
  type?: string
  includeArchived?: boolean
}) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.aircraft.getAll.queryOptions({
      input: input ?? {},
    })
  )
}

export function useAircraftGetById(id: string) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.aircraft.getById.queryOptions({ 
      input: { id },
      enabled: !!id
    })
  )
}

export function useAircraftCreate() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.aircraft.create.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key() })
      },
    })
  )
}

export function useAircraftUpdate(aircraftId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.aircraft.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key({input: {id: aircraftId}}) })
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.getAll.key() })

      },
    })
  )
}

export function useAircraftSetPhoto(aircraftId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.aircraft.setPhoto.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key({ input: { id: aircraftId } }) })
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.getAll.key() })
      },
    })
  )
}

// Attach / detach a photo to one of an aircraft's facility/preference items.
export function useAircraftAddFacilityPhoto(aircraftId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.aircraft.addFacilityPhoto.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key({ input: { id: aircraftId } }) })
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.getAll.key() })
      },
    })
  )
}

export function useAircraftRemoveFacilityPhoto(aircraftId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.aircraft.removeFacilityPhoto.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key({ input: { id: aircraftId } }) })
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.getAll.key() })
      },
    })
  )
}

export function useAircraftArchive() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.aircraft.archive.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key() })
      },
    })
  )
}

export function useAircraftUnarchive() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.aircraft.unarchive.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key() })
      },
    })
  )
}

export function useAircraftGetDevicePasswords(aircraftId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(
    orpc.aircraft.getDevicePasswords.queryOptions({
      input: { aircraftId },
      enabled: !!aircraftId
    })
  )
}

export function useAircraftAddDevicePassword(aircraftId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.aircraft.addDevicePassword.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.aircraft.getDevicePasswords.key({ input: { aircraftId } })
        })
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key() })
      },
    })
  )
}

export function useAircraftRemoveDevicePassword(aircraftId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.aircraft.removeDevicePassword.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.aircraft.getDevicePasswords.key({ input: { aircraftId } })
        })
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key() })
      },
    })
  )
}

export function useAircraftAddDocument(aircraftId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.aircraft.addDocument.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key({ input: { id: aircraftId } }) })
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.getAll.key() })
        queryClient.invalidateQueries({ queryKey: orpc.document.key() })
      },
    })
  )
}

export function useAircraftRemoveDocument(aircraftId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.aircraft.removeDocument.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.key({ input: { id: aircraftId } }) })
        queryClient.invalidateQueries({ queryKey: orpc.aircraft.getAll.key() })
        queryClient.invalidateQueries({ queryKey: orpc.document.key() })
      },
    })
  )
}
